import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  FileText, Download, Clock, Eye, AlertTriangle, 
  CheckCircle2, XCircle, Loader2, Shield
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareReportPage
 * 
 * Public page for viewing/downloading shared PDF reports.
 * No authentication required.
 * Route: /share/:token
 */
const ShareReportPage = () => {
  const { token } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/public/report/share/${token}/status`);
        const data = await response.json();
        setStatus(data);
      } catch (err) {
        console.error("Failed to fetch share status:", err);
        setStatus({
          ok: false,
          valid: false,
          error_code: "NETWORK_ERROR",
          error_message: "Unable to verify share link. Please try again."
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/report/share/${token}/pdf`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to download PDF');
      }
      
      // Get filename from header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'AIMB_Report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleView = () => {
    // Open PDF in new tab
    window.open(`${API_URL}/api/public/report/share/${token}/pdf`, '_blank');
  };

  const formatExpiry = (isoDate) => {
    if (!isoDate) return 'Unknown';
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch {
      return isoDate;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying share link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">AI Means Business</h1>
              <p className="text-xs text-slate-400">Shared Report Access</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Valid Share Link */}
        {status?.valid ? (
          <div className="space-y-8">
            {/* Success Banner */}
            <div className="glass p-6 rounded-xl border border-green-500/30" data-testid="share-valid">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white mb-1">
                    Shared Report Available
                  </h2>
                  <p className="text-slate-400">
                    You have been granted access to view this confidential report.
                  </p>
                </div>
              </div>
            </div>

            {/* Report Info Card */}
            <div className="glass p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-white mb-4">Report Details</h3>
              
              <div className="space-y-3">
                {status.business_name && (
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-sm w-32">Client:</span>
                    <span className="text-white">{status.business_name}</span>
                  </div>
                )}
                
                {status.service_name && (
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-sm w-32">Service:</span>
                    <span className="text-white">{status.service_name}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-3">
                  <span className="text-slate-400 text-sm w-32">Link Expires:</span>
                  <span className="text-amber-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {formatExpiry(status.expires_at)}
                  </span>
                </div>
                
                {status.remaining_views !== null && status.remaining_views !== undefined && (
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-sm w-32">Views Remaining:</span>
                    <span className="text-blue-400 flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      {status.remaining_views}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleView}
                className="flex-1 bg-blue-600 hover:bg-blue-700 py-6"
                data-testid="view-pdf-btn"
              >
                <Eye className="w-5 h-5 mr-2" />
                View Report
              </Button>
              
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="flex-1 bg-green-600 hover:bg-green-700 py-6"
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>

            {/* Security Notice */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-400">
                  <p className="font-medium text-slate-300 mb-1">Confidential Document</p>
                  <p>
                    This report is shared for your review only. Do not redistribute 
                    without authorization. Access is logged for security purposes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Invalid/Expired Share Link */
          <div className="max-w-lg mx-auto" data-testid="share-invalid">
            <div className="glass p-8 rounded-xl border border-red-500/30 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-2">
                {status?.error_code === 'EXPIRED' && 'Link Expired'}
                {status?.error_code === 'REVOKED' && 'Link Revoked'}
                {status?.error_code === 'MAX_VIEWS_REACHED' && 'View Limit Reached'}
                {status?.error_code === 'INVALID_TOKEN' && 'Invalid Link'}
                {!['EXPIRED', 'REVOKED', 'MAX_VIEWS_REACHED', 'INVALID_TOKEN'].includes(status?.error_code) && 'Access Denied'}
              </h2>
              
              <p className="text-slate-400 mb-6">
                {status?.error_message || 'This share link is no longer valid.'}
              </p>
              
              {status?.expires_at && status?.error_code === 'EXPIRED' && (
                <p className="text-sm text-slate-500 mb-6">
                  Expired on: {formatExpiry(status.expires_at)}
                </p>
              )}
              
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  If you believe this is an error, please contact the person who shared this link.
                </p>
                
                <Link to="/">
                  <Button variant="outline" className="mt-4">
                    Go to AI Means Business
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-slate-500">
          <p>© 2026 Use What You Got Publishing, LLC</p>
          <p className="mt-1">
            <a href="https://www.AIMeansBusiness.net" className="text-primary hover:underline">
              www.AIMeansBusiness.net
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ShareReportPage;
